import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type React from 'react';

import type { ChatMessage } from 'containers/admin/clients/client/tabs-components/ai-assistant/hooks/use-active-input-part';

interface AiAssistantSessionContextValue {
  getMessagesForConversation: (conversationId: string) => ChatMessage[];
  setMessagesForConversation: (
    conversationId: string,
    updater: (messages: ChatMessage[]) => ChatMessage[],
  ) => void;
  getStreamingMessageId: (conversationId: string) => string | null;
  setStreamingForConversation: (
    conversationId: string,
    messageId: string | null,
  ) => void;
}

interface AiAssistantSessionProviderProps {
  children: ReactNode;
}

/**
 * Session manager for AI Assistant: messages and streaming state per conversation.
 * - Messages are keyed by conversationId so switching sessions preserves state.
 * - Streaming state is per conversation: input is disabled only for the current
 *   session when it has an active stream; other sessions stay editable.
 * - When returning to a session, if its stream is still in progress, loading state is shown.
 */
const AiAssistantSessionContext =
  createContext<AiAssistantSessionContextValue | null>(null);

export const useAiAssistantSession = (): AiAssistantSessionContextValue => {
  const ctx = useContext(AiAssistantSessionContext);

  if (!ctx) {
    throw new Error(
      'useAiAssistantSession must be used within AiAssistantSessionProvider',
    );
  }

  return ctx;
};

export const AiAssistantSessionProvider = ({
  children,
}: AiAssistantSessionProviderProps): React.JSX.Element => {
  const [messagesByConversationId, setMessagesByConversationId] = useState<
    Record<string, ChatMessage[]>
  >({});
  const [streamingByConversationId, setStreamingByConversationId] = useState<
    Record<string, string>
  >({});

  const setMessagesForConversation = useCallback(
    (
      conversationId: string,
      updater: (messages: ChatMessage[]) => ChatMessage[],
    ) => {
      setMessagesByConversationId((prev) => ({
        ...prev,
        [conversationId]: updater(prev[conversationId] ?? []),
      }));
    },
    [],
  );

  const getMessagesForConversation = useCallback(
    (conversationId: string): ChatMessage[] =>
      messagesByConversationId[conversationId] ?? [],
    [messagesByConversationId],
  );

  const setStreamingForConversation = useCallback(
    (conversationId: string, messageId: string | null) => {
      setStreamingByConversationId((prev) => {
        const next = { ...prev };

        if (messageId == null) {
          delete next[conversationId];
        } else {
          next[conversationId] = messageId;
        }

        return next;
      });
    },
    [],
  );

  const getStreamingMessageId = useCallback(
    (conversationId: string): string | null =>
      streamingByConversationId[conversationId] ?? null,
    [streamingByConversationId],
  );

  const value: AiAssistantSessionContextValue = {
    getMessagesForConversation,
    setMessagesForConversation,
    getStreamingMessageId,
    setStreamingForConversation,
  };

  return (
    <AiAssistantSessionContext.Provider value={value}>
      {children}
    </AiAssistantSessionContext.Provider>
  );
};
